import type { ReactNode } from 'react';
import { HelpContentKey } from '../constants/helpContent';
import { FEATURE_FLAG_KEYS } from '../contexts/FeatureFlagsContext';
import { workspaceMenuIconFor } from './workspaceMenuIcons';
import {
  compactMenuItems,
  menuDivider,
  menuItemFromSpec,
  WorkspaceMenuItem,
  WorkspaceMenuSection,
  WorkspaceQuickAction,
} from '../types/workspaceMenu';

export type WorkspaceMenuBuildContext = {
  tooltipFor: (key: HelpContentKey, enabled: boolean) => string;
  isFeatureEnabled: (key: string) => boolean;
  effectiveDocumentReadOnly: boolean;
  tabsLength: number;
  selectedContentsLength: number;
  canUndo: boolean;
  canRedo: boolean;
  undoOperationName: string;
  redoOperationName: string;
  canCut: () => boolean;
  canCopy: () => boolean;
  canPaste: () => boolean;
  cutHelpKey: HelpContentKey;
  copyHelpKey: HelpContentKey;
  pasteHelpKey: HelpContentKey;
  invertSelectionHelpTooltip: string;
  canAddCategory: () => boolean;
  canRenameCategory: () => boolean;
  canDeleteCategory: () => boolean;
  canSplitCategory: () => boolean;
  canGroupCategories: () => boolean;
  canMergeCategories: () => boolean;
  canFlattenCategory: () => boolean;
  canCollectBranchContent: () => boolean;
  canImportSubtree: () => boolean;
  canMoveCategoryUp: () => boolean;
  canMoveCategoryDown: () => boolean;
  hasSelectedCategory: boolean;
  canDuplicateAsDocument: () => boolean;
  canAddContent: () => boolean;
  canEditContent: () => boolean;
  canAdjustTextContent: () => boolean;
  canDeleteContent: () => boolean;
  canMoveContentUp: () => boolean;
  canMoveContentDown: () => boolean;
  canInvertContentSelection: () => boolean;
  canRenameSubcase: () => boolean;
  canManageSubcaseGroups: () => boolean;
  canFilterContentPane: () => boolean;
  contentFilterActiveTooltip: string;
  canFindInDocument: () => boolean;
  canShowSubTree: () => boolean;
  canOrganizeCategories: () => boolean;
  canAddMatchingContent: () => boolean;
  canCompareContent: () => boolean;
  canCategoryAiReport: () => boolean;
  canAskAi: () => boolean;
  canTreeAssistant: () => boolean;
  onUndo: () => void;
  onRedo: () => void;
  onCut: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onAddCategory: () => void;
  onRenameCategory: () => void;
  onDeleteCategory: () => void;
  onSplitCategory: () => void;
  onGroupCategories: () => void;
  onMergeCategories: () => void;
  onFlattenCategory: () => void;
  onCollectBranchContent: () => void;
  onImportSubtree: () => void;
  onMoveCategoryUp: () => void;
  onMoveCategoryDown: () => void;
  onDuplicateAsDocument: () => void;
  onAddContent: () => void;
  onEditContent: () => void;
  onAdjustTextContent: () => void;
  onDeleteContent: () => void;
  onMoveContentUp: () => void;
  onMoveContentDown: () => void;
  onInvertContentSelection: () => void;
  onRenameSubcase: () => void;
  onManageSubcaseGroups: () => void;
  onFilterContentPane: () => void;
  onFindInDocument: () => void;
  onShowSubTree: () => void;
  onOrganizeCategories: () => void;
  onAddMatchingContent: () => void;
  onCompareContent: () => void;
  onCategoryAiReport: () => void;
  onAskAi: () => void;
  onTreeAssistant: () => void;
  quickActionIcons: {
    add: ReactNode;
    edit: ReactNode;
    delete: ReactNode;
    split: ReactNode;
    rename: ReactNode;
    moveUp: ReactNode;
    moveDown: ReactNode;
    cut: ReactNode;
    copy: ReactNode;
    paste: ReactNode;
    undo: ReactNode;
    redo: ReactNode;
  };
  canQuickEdit: () => boolean;
  canQuickDelete: () => boolean;
  canQuickMoveUp: () => boolean;
  canQuickMoveDown: () => boolean;
  onQuickEdit: () => void;
  onQuickDelete: () => void;
  onQuickRename: () => void;
  onQuickMoveUp: () => void;
  onQuickMoveDown: () => void;
};

function collectSpecs(
  ctx: WorkspaceMenuBuildContext,
  specs: Parameters<typeof menuItemFromSpec>[0][],
): WorkspaceMenuItem[] {
  return compactMenuItems(
    specs
      .map((spec) => menuItemFromSpec(spec, ctx.tooltipFor, workspaceMenuIconFor))
      .filter((item): item is WorkspaceMenuItem => item != null),
  );
}

export function buildWorkspaceMenuSections(ctx: WorkspaceMenuBuildContext): WorkspaceMenuSection[] {
  const ro = ctx.effectiveDocumentReadOnly;
  const aiMaster = ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.AI_FEATURES_MASTER);

  const editItems = collectSpecs(ctx, [
    {
      id: 'undo',
      label: ctx.canUndo ? `Undo ${ctx.undoOperationName}` : 'Undo',
      helpKey: ctx.canUndo ? 'undo' : 'undo-disabled',
      enabled: ctx.canUndo,
      onClick: ctx.onUndo,
      hidden: ro,
    },
    {
      id: 'redo',
      label: ctx.canRedo ? `Redo ${ctx.redoOperationName}` : 'Redo',
      helpKey: ctx.canRedo ? 'redo' : 'redo-disabled',
      enabled: ctx.canRedo,
      onClick: ctx.onRedo,
      hidden: ro,
    },
  ]);

  if (!ro) {
    editItems.push(menuDivider());
    editItems.push(
      ...collectSpecs(ctx, [
        {
          id: 'cut',
          label: ctx.selectedContentsLength > 0 ? 'Cut content' : 'Cut category',
          helpKey: ctx.cutHelpKey,
          enabled: ctx.canCut(),
          onClick: ctx.onCut,
        },
        {
          id: 'copy',
          label: ctx.selectedContentsLength > 0 ? 'Copy content' : 'Copy category',
          helpKey: ctx.copyHelpKey,
          enabled: ctx.canCopy(),
          onClick: ctx.onCopy,
        },
        {
          id: 'paste',
          label: 'Paste',
          helpKey: ctx.pasteHelpKey,
          enabled: ctx.canPaste(),
          onClick: ctx.onPaste,
        },
      ]),
    );
  }

  const categoryItems = collectSpecs(ctx, [
    {
      id: 'add-category',
      label: 'Add category',
      helpKey: 'add-category',
      enabled: ctx.canAddCategory(),
      onClick: ctx.onAddCategory,
      hidden: ro,
    },
    {
      id: 'rename-category',
      label: 'Rename category',
      helpKey: 'rename-category',
      enabled: ctx.canRenameCategory(),
      onClick: ctx.onRenameCategory,
      hidden: ro,
    },
    {
      id: 'delete-category',
      label: 'Delete category',
      helpKey: 'delete-category',
      enabled: ctx.canDeleteCategory(),
      onClick: ctx.onDeleteCategory,
      hidden: ro,
    },
  ]);

  if (!ro) {
    categoryItems.push(menuDivider());
    categoryItems.push(
      ...collectSpecs(ctx, [
        {
          id: 'split',
          label: 'Split category',
          helpKey: 'split-category',
          enabled: ctx.canSplitCategory(),
          onClick: ctx.onSplitCategory,
        },
        {
          id: 'group',
          label: 'Group categories',
          helpKey: 'group-categories',
          enabled: ctx.canGroupCategories(),
          onClick: ctx.onGroupCategories,
        },
        {
          id: 'merge',
          label: 'Merge categories',
          helpKey: 'merge-categories',
          enabled: ctx.canMergeCategories(),
          onClick: ctx.onMergeCategories,
        },
        {
          id: 'flatten',
          label: 'Flatten category',
          helpKey: 'flatten-category',
          enabled: ctx.canFlattenCategory(),
          onClick: ctx.onFlattenCategory,
        },
        {
          id: 'collect-branch',
          label: 'Collect branch content',
          helpKey: 'collapse-subtree-to-content-category',
          enabled: ctx.canCollectBranchContent(),
          onClick: ctx.onCollectBranchContent,
        },
        {
          id: 'import-subtree',
          label: 'Import branch from document…',
          helpKey: 'import-subtree-from-document',
          enabled: ctx.canImportSubtree(),
          onClick: ctx.onImportSubtree,
        },
      ]),
    );
    categoryItems.push(menuDivider());
    categoryItems.push(
      ...collectSpecs(ctx, [
        {
          id: 'move-category-up',
          label: 'Move up',
          helpKey: 'move-category-up',
          enabled: ctx.canMoveCategoryUp(),
          onClick: ctx.onMoveCategoryUp,
        },
        {
          id: 'move-category-down',
          label: 'Move down',
          helpKey: 'move-category-down',
          enabled: ctx.canMoveCategoryDown(),
          onClick: ctx.onMoveCategoryDown,
        },
      ]),
    );
  }

  categoryItems.push(menuDivider());
  categoryItems.push(
    ...collectSpecs(ctx, [
      {
        id: 'duplicate-as-document',
        label: 'Duplicate as document',
        helpKey: 'duplicate-as-document',
        enabled: ctx.canDuplicateAsDocument(),
        onClick: ctx.onDuplicateAsDocument,
      },
    ]),
  );

  const contentItems = collectSpecs(ctx, [
    {
      id: 'add-content',
      label: 'Add content',
      helpKey: 'add-content',
      enabled: ctx.canAddContent(),
      onClick: ctx.onAddContent,
      hidden: ro,
    },
    {
      id: 'edit-content',
      label: 'Edit content',
      helpKey: 'edit-content',
      enabled: ctx.canEditContent(),
      onClick: ctx.onEditContent,
      hidden: ro,
    },
    {
      id: 'adjust-text',
      label: 'Adjust text…',
      helpKey: 'adjust-text-content',
      enabled: ctx.canAdjustTextContent(),
      onClick: ctx.onAdjustTextContent,
      hidden: ro,
    },
    {
      id: 'delete-content',
      label: 'Delete content',
      helpKey: 'delete-content',
      enabled: ctx.canDeleteContent(),
      onClick: ctx.onDeleteContent,
      hidden: ro,
    },
  ]);

  if (!ro) {
    contentItems.push(menuDivider());
    contentItems.push(
      ...collectSpecs(ctx, [
        {
          id: 'move-content-up',
          label: 'Move up',
          helpKey: 'move-content-up',
          enabled: ctx.canMoveContentUp(),
          onClick: ctx.onMoveContentUp,
        },
        {
          id: 'move-content-down',
          label: 'Move down',
          helpKey: 'move-content-down',
          enabled: ctx.canMoveContentDown(),
          onClick: ctx.onMoveContentDown,
        },
        {
          id: 'invert-content-selection',
          label: 'Invert selection',
          helpKey: 'edit-content',
          enabled: ctx.canInvertContentSelection(),
          onClick: ctx.onInvertContentSelection,
        },
      ]),
    );
  }

  const subcaseItems = collectSpecs(ctx, [
    {
      id: 'rename-subcase',
      label: 'Rename subcase',
      helpKey: 'rename-subcase',
      enabled: ctx.canRenameSubcase(),
      onClick: ctx.onRenameSubcase,
      hidden: ro,
    },
    {
      id: 'subcase-groups',
      label: 'Manage subcase groups',
      helpKey: 'manage-subcase-groups',
      enabled: ctx.canManageSubcaseGroups(),
      onClick: ctx.onManageSubcaseGroups,
      hidden: ro,
    },
    {
      id: 'content-filter',
      label: 'Filter content pane by subcase',
      helpKey: 'content-pane-subcase-filter',
      enabled: ctx.canFilterContentPane(),
      onClick: ctx.onFilterContentPane,
      hidden: !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.FILTER_CONTENT_PANE_BY_SUBCASE),
    },
  ]);

  const aiItems = compactMenuItems([
    ...(collectSpecs(ctx, [
      {
        id: 'structure-categories',
        label: 'Structure categories (AI)',
        helpKey: 'structure-categories',
        enabled: ctx.canOrganizeCategories(),
        onClick: ctx.onOrganizeCategories,
        hidden:
          ro || !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.GENERATE_SUBTREE_FROM_CONTENT),
      },
      {
        id: 'add-matching-content',
        label: 'Add matching content (AI)',
        helpKey: 'add-matching-content',
        enabled: ctx.canAddMatchingContent(),
        onClick: ctx.onAddMatchingContent,
        hidden: ro || !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.SEMANTIC_FIND),
      },
      {
        id: 'compare-content',
        label: 'Compare content and select',
        helpKey: 'compare-content',
        enabled: ctx.canCompareContent(),
        onClick: ctx.onCompareContent,
        hidden: ro || !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.COMPARE_CONTENT_SELECTION),
      },
      {
        id: 'category-ai-report',
        label: 'Generate report (AI)…',
        helpKey: 'category-ai-report',
        enabled: ctx.canCategoryAiReport(),
        onClick: ctx.onCategoryAiReport,
        hidden: !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.CATEGORY_AI_REPORT),
      },
    ]) as WorkspaceMenuItem[]),
    ...(aiMaster && !ro
      ? ([menuDivider()] as WorkspaceMenuItem[])
      : []),
    ...collectSpecs(ctx, [
      {
        id: 'ask-ai',
        label: 'Ask AI',
        helpKey: 'ask-ai',
        enabled: ctx.canAskAi(),
        onClick: ctx.onAskAi,
        hidden: !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.ASK_AI),
      },
      {
        id: 'tree-assistant',
        label: 'Structure builder',
        helpKey: 'tree-assistant',
        enabled: ctx.canTreeAssistant(),
        onClick: ctx.onTreeAssistant,
        hidden: ro || !ctx.isFeatureEnabled(FEATURE_FLAG_KEYS.TREE_ASSISTANT),
      },
    ]),
  ]);

  const viewItems = collectSpecs(ctx, [
    {
      id: 'find',
      label: 'Find in document',
      helpKey: 'find-in-document',
      enabled: ctx.canFindInDocument(),
      onClick: ctx.onFindInDocument,
    },
    {
      id: 'show-sub-tree',
      label: 'Category Structure Overview',
      helpKey: 'show-sub-tree',
      enabled: ctx.canShowSubTree(),
      onClick: ctx.onShowSubTree,
    },
  ]);

  return [
    { id: 'edit', label: 'Edit', items: editItems, hidden: ro && editItems.length === 0 },
    { id: 'category', label: 'Category', items: categoryItems },
    { id: 'content', label: 'Content', items: contentItems },
    { id: 'subcase', label: 'Subcase', items: subcaseItems, hidden: subcaseItems.length === 0 },
    {
      id: 'ai',
      label: 'AI',
      items: aiItems,
      hidden: !aiMaster || aiItems.length === 0,
    },
    { id: 'view', label: 'View', items: viewItems },
  ].filter((section) => !section.hidden && section.items.length > 0);
}

export function buildWorkspaceQuickActions(ctx: WorkspaceMenuBuildContext): WorkspaceQuickAction[] {
  const ro = ctx.effectiveDocumentReadOnly;
  const editSingleContent = ctx.selectedContentsLength === 1;
  const actions: WorkspaceQuickAction[] = [
    {
      id: 'add-category',
      label: 'Add category',
      disabled: !ctx.canAddCategory(),
      helpTooltip: ctx.tooltipFor('add-category', ctx.canAddCategory()),
      onClick: ctx.onAddCategory,
      icon: ctx.quickActionIcons.add,
    },
    {
      id: editSingleContent ? 'edit-content' : 'add-content',
      label: editSingleContent ? 'Edit content' : 'Add content',
      disabled: editSingleContent ? !ctx.canEditContent() : !ctx.canAddContent(),
      helpTooltip: ctx.tooltipFor(
        editSingleContent ? 'edit-content' : 'add-content',
        editSingleContent ? ctx.canEditContent() : ctx.canAddContent(),
      ),
      onClick: ctx.onAddContent,
      icon: ctx.quickActionIcons.edit,
    },
    {
      id: 'delete',
      label: 'Delete',
      disabled: !ctx.canQuickDelete(),
      helpTooltip: ctx.tooltipFor(
        ctx.selectedContentsLength > 0 ? 'delete-content' : 'delete-category',
        ctx.canQuickDelete(),
      ),
      onClick: ctx.onQuickDelete,
      icon: ctx.quickActionIcons.delete,
    },
    {
      id: 'split',
      label: 'Split',
      disabled: !ctx.canSplitCategory(),
      helpTooltip: ctx.tooltipFor('split-category', ctx.canSplitCategory()),
      onClick: ctx.onSplitCategory,
      icon: ctx.quickActionIcons.split,
    },
    {
      id: 'rename',
      label: 'Rename',
      disabled: !ctx.canRenameCategory(),
      helpTooltip: ctx.tooltipFor('rename-category', ctx.canRenameCategory()),
      onClick: ctx.onQuickRename,
      icon: ctx.quickActionIcons.rename,
    },
    {
      id: ctx.selectedContentsLength > 0 ? 'move-content-up' : 'move-category-up',
      label: 'Move up',
      disabled: !ctx.canQuickMoveUp(),
      helpTooltip: ctx.tooltipFor(
        ctx.selectedContentsLength > 0 ? 'move-content-up' : 'move-category-up',
        ctx.canQuickMoveUp(),
      ),
      onClick: ctx.onQuickMoveUp,
      icon: ctx.quickActionIcons.moveUp,
    },
    {
      id: ctx.selectedContentsLength > 0 ? 'move-content-down' : 'move-category-down',
      label: 'Move down',
      disabled: !ctx.canQuickMoveDown(),
      helpTooltip: ctx.tooltipFor(
        ctx.selectedContentsLength > 0 ? 'move-content-down' : 'move-category-down',
        ctx.canQuickMoveDown(),
      ),
      onClick: ctx.onQuickMoveDown,
      icon: ctx.quickActionIcons.moveDown,
    },
    {
      id: 'cut',
      label: 'Cut',
      disabled: !ctx.canCut(),
      helpTooltip: ctx.tooltipFor(ctx.cutHelpKey, ctx.canCut()),
      onClick: ctx.onCut,
      icon: ctx.quickActionIcons.cut,
    },
    {
      id: 'copy',
      label: 'Copy',
      disabled: !ctx.canCopy(),
      helpTooltip: ctx.tooltipFor(ctx.copyHelpKey, ctx.canCopy()),
      onClick: ctx.onCopy,
      icon: ctx.quickActionIcons.copy,
    },
    {
      id: 'paste',
      label: 'Paste',
      disabled: !ctx.canPaste(),
      helpTooltip: ctx.tooltipFor(ctx.pasteHelpKey, ctx.canPaste()),
      onClick: ctx.onPaste,
      icon: ctx.quickActionIcons.paste,
    },
    {
      id: 'undo',
      label: 'Undo',
      disabled: !ctx.canUndo,
      helpTooltip: ctx.canUndo
        ? `Undo ${ctx.undoOperationName}`
        : ctx.tooltipFor('undo-disabled', false),
      onClick: ctx.onUndo,
      icon: ctx.quickActionIcons.undo,
    },
    {
      id: 'redo',
      label: 'Redo',
      disabled: !ctx.canRedo,
      helpTooltip: ctx.canRedo
        ? `Redo ${ctx.redoOperationName}`
        : ctx.tooltipFor('redo-disabled', false),
      onClick: ctx.onRedo,
      icon: ctx.quickActionIcons.redo,
    },
  ];
  return ro ? [] : actions;
}
